import { defineStore } from "pinia";
import router from "@/router";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useCurrentStore } from "@/stores/current";
import { useNotificationStore } from "@/stores/notification.store";
import { i18n } from "@/main";
import { useUserStore } from "@/stores/user.store"; // ⬅️ do pobrania updated_at
import { isPersistSessionOnReload } from "@/config/appVersion";
import { clearImageCache } from "@/helpers/vehicleImageCache";
import { useLocationSessionStore } from "@/stores/locationSession";

const SESSION_TOUCH_INTERVAL_MS = 20000;
// Co ile widoczna karta podtrzymuje sesję bez interakcji (pasywny podgląd). Karta ukryta sesji nie podtrzymuje.
// Krócej niż SESSION_TOUCH_INTERVAL_MS — /ping i tak wychodzi najwyżej raz na 20 s, a odstęp musi zmieścić się
// w APP_SESSION_LIFE_TIME (domyślnie 60 s).
const VISIBLE_TAB_HEARTBEAT_MS = 15000;

// Kod błędu backendu „użytkownik jest już zalogowany" (ErrorCode::USER_ALREADY_LOGGED_IN).
const USER_ALREADY_LOGGED_IN = 1003;
// Bilet „Wyloguj mnie" wygasa na backendzie po kilku minutach — chowamy przycisk nieco wcześniej.
const FORCE_LOGOUT_OFFER_MS = 4 * 60 * 1000;
// Uchwyt poza stanem Pinia (wartość nieserializowalna).
let forceLogoutOfferTimer = null;

// Store Pinia odpowiadający za uwierzytelnianie użytkownika oraz sesję
export const useAuthStore = defineStore({
  id: "auth",
  state: () => ({
    token: sessionStorage.getItem("cvToken"), // Token JWT
    returnUrl: "", // Redirect po logowaniu
    pingLoop: false, // ID interwału pingowania
    current: null, // opcjonalnie bieżący user
    mustChangePassword: sessionStorage.getItem("mustChangePwd") === "1", // <---
    notify: useNotificationStore(),
    skipPwdFreshCheckOnce: false,   // przepuść najbliższą nawigację bez sprawdzania ważności
    localPasswordChangedAt: null,   // lokalny „znacznik czasu” zmiany hasła
    suspendHeartbeatsUntilUserAction: false,
    sessionTouchEnabled: false,
    lastSessionTouchAt: 0,
    // Stan między widokami SMS-resetu: numer telefonu, dla którego wysłano kod.
    // Trzymany tylko w pamięci (znika po refresh) — celowo NIE w URL/sessionStorage,
    // bo to PII i trafiłoby do historii przeglądarki.
    pendingSmsReset: null,
    // Stan między ekranem logowania a wpisaniem kodu 2FA. `phoneMasked`/`expiresAt`/
    // `resendInSeconds` przychodzą z backendu po pierwszym etapie. `requiresPhoneSetup`
    // = true, gdy admin ma świeże konto bez nr telefonu i front musi pokazać ekran
    // ustawienia numeru przed kodem. Trzymany tylko w pamięci (znika po refresh) —
    // jeśli user odświeży kartę, idzie z powrotem na /login (sesja BE wymusi 2FA ponownie).
    pendingTwoFactor: null,
    // Oferta zamknięcia pozostawionej sesji („Wyloguj mnie") po odmowie logowania z kodem 1003:
    // `{ username, token }`. Tylko w pamięci — bilet jest krótkotrwały i jednorazowy.
    pendingForceLogout: null,
  }),

  actions: {
    // helper
    _setMustChangePassword(val) {
      this.mustChangePassword = !!val;
      if (val) sessionStorage.setItem("mustChangePwd", "1");
      else sessionStorage.removeItem("mustChangePwd");
    },

    // Rozstrzyga, dokąd przekierować po udanym logowaniu.
    //
    // `returnUrl` jest dwuznaczne: router pre-loginowy ustawia go na "/archival" (SG)
    // lub "/" (non-SG) jako *default* — to NIE jest świadomy wybór usera. Świadomy
    // wybór to każda inna ścieżka (user kliknął chroniony link, guard wyrzucił go
    // na /login → ścieżka zapamiętana w returnUrl).
    //
    // Dla wartości domyślnych delegujemy do `userStore.landingPath()`, który zna role
    // i SG-vs-non-SG. Dla każdego innego returnUrl honorujemy wybór usera (zakłada,
    // że jeśli nie ma do tego uprawnień, beforeEach `meta.access` go odepchnie).
    resolvePostLoginTarget(userStore) {
      const ret = typeof this.returnUrl === "string" ? this.returnUrl : "";
      const PRE_LOGIN_DEFAULTS = ["", "/", "/archival", "/login"];
      if (!PRE_LOGIN_DEFAULTS.includes(ret)) {
        return ret;
      }
      return userStore.landingPath();
    },

    // 90 dni
    _isPasswordExpired(updatedAt) {
      if (!updatedAt) return true;
      const ts = new Date(updatedAt).getTime();
      return Date.now() - ts > 90 * 24 * 60 * 60 * 1000;
    },

    // Wznowienie sesji po przeładowaniu strony (F5).
    //
    // Po reloadzie Pinia startuje od zera — `startInactivityWatch()`/`ping()` (odpalane
    // normalnie tylko przy logowaniu) nie zostały wznowione. Jeśli w sessionStorage jest
    // token (a w wersji testowej sesja BE nie została ubita przez sendLeaveRequest),
    // odtwarzamy nasłuch aktywności i utrzymywanie sesji, żeby użytkownik pozostał zalogowany.
    //
    // W wersji nietestowej sesja BE została i tak zniszczona przez `DELETE /login` w
    // beforeunload, więc `ping(false)` dostanie 401 → logout — zachowanie jak dotychczas.
    rehydrateSession() {
      if (!this.token) {
        return;
      }
      this.startInactivityWatch();
      this.ping(false);
    },

    // Kontekst potrzebny do zbudowania aplikacji w finalnym kształcie: role (→ `landingPath()`,
    // `hasAccess`) ORAZ flagi funkcji, na których menu gate'uje część pozycji.
    //
    // Jedno żądanie: `GET /users/self` zwraca `featureFlags` obok profilu, a `self()`
    // przekazuje je do config store'a. Dlatego przygotowanie kontekstu NIC nie dokłada do
    // czasu logowania — `self()` jest idempotentne i w `login()` zdążył go już wywołać
    // `ensurePasswordFreshness()`, więc tutaj zwraca gotowe dane bez ruchu po sieci.
    //
    // Błąd jest tłumiony świadomie: nieudane pobranie profilu nie może zablokować
    // logowania (dostęp weryfikują guardy tras i backend).
    async prepareAppContext(userStore = useUserStore()) {
      await userStore.self().catch(() => {});
    },

    // Logowanie użytkownika - pobiera token, zapisuje go do sessionStorage
      async login(username, password) {
          this._setPendingForceLogout(null);
          try {
              const authRes = await fetchWrapper.postWithErrorData(
                  `${process.env.VUE_APP_API_URL}/login`,
                  { username, password }
              );

              // Drugi etap (SMS 2FA). BE odpowiedział `{ requires2fa: true, preAuthToken, ... }`
              // zamiast pełnego tokena — przechodzimy na ekran wpisania kodu (lub ustawienia nr).
              // `preAuthToken` używamy jako Authorization: Bearer dla endpointów /login/2fa/*,
              // bo standardowy `authStore.token` jest jeszcze pusty (dostaniemy go po verify).
              if (authRes && authRes.requires2fa) {
                  this.pendingTwoFactor = {
                      preAuthToken: authRes.preAuthToken ?? null,
                      requiresPhoneSetup: !!authRes.requiresPhoneSetup,
                      challenge: authRes.challenge ?? null,
                      dispatchFailed: !!authRes.dispatchFailed,
                  };
                  router.push(
                      authRes.requiresPhoneSetup ? "/login/2fa/setup-phone" : "/login/2fa"
                  );
                  return;
              }

              this.token = authRes.token;
              sessionStorage.setItem("cvToken", authRes.token);

              // >>> KLUCZ: wyczyść poprzednie dane usera
              const userStore = useUserStore();
              userStore.reset();

              const passwordExpired = await this.ensurePasswordFreshness();

              if (passwordExpired) {
                  this.notify.warning(i18n.global.t("password-expired-set-new"));
                  router.push("/password-expired");
              } else {
                  this.startInactivityWatch();
                  this.ping(false);

                  // Załaduj profil zanim wybierzemy trasę — `landingPath()` patrzy na role.
                  // Razem z nim flagi funkcji: menu musi się zbudować od razu w finalnym
                  // kształcie. Bez tego pozycje gate'owane flagą (np. „Zarządzanie
                  // lokalizacjami") pojawiały się dopiero po odpowiedzi z
                  // `/app/feature-flags`, już po wyrenderowaniu paska nawigacji.
                  const userStoreForLanding = useUserStore();
                  await this.prepareAppContext(userStoreForLanding);
                  const target = this.resolvePostLoginTarget(userStoreForLanding);
                  router.push(target);
              }
          } catch (error) {
              // Konto ma pozostawioną sesję (np. zamknięty komputer bez wylogowania). Hasło było
              // poprawne, więc backend dołączył bilet — ekran logowania pokaże „Wyloguj mnie".
              const details = Array.isArray(error?.errors) ? error.errors[0] : null;
              if (details?.code === USER_ALREADY_LOGGED_IN && details.forceLogoutToken) {
                  this._setPendingForceLogout({ username, token: details.forceLogoutToken });
              }
              this.notify.error(error);
          }
      },

      _setPendingForceLogout(value) {
          clearTimeout(forceLogoutOfferTimer);
          forceLogoutOfferTimer = null;
          this.pendingForceLogout = value;
          if (value) {
              forceLogoutOfferTimer = setTimeout(() => {
                  this.pendingForceLogout = null;
              }, FORCE_LOGOUT_OFFER_MS);
          }
      },

      // Zamyka pozostawioną sesję konta z ekranu logowania. Nie loguje — użytkownik
      // loguje się potem zwykłym przyciskiem (pełny przebieg, łącznie z 2FA).
      async forceLogout() {
          const offer = this.pendingForceLogout;
          if (!offer) return;
          try {
              await fetchWrapper.post(
                  `${process.env.VUE_APP_API_URL}/login/force-logout`,
                  { token: offer.token }
              );
              this.notify.success(i18n.global.t("force-logout-success"));
          } catch (error) {
              this.notify.error(i18n.global.t("force-logout-failed"));
          } finally {
              this._setPendingForceLogout(null);
          }
      },

      // --- Drugi etap logowania (SMS 2FA) -----------------------------------------
      //
      // Sesja BE jest oznaczona flagą `_2fa_pending` po pierwszym etapie. Każdy z poniższych
      // wywołań idzie na endpointy `/api/v1/login/2fa/*` które są jedynymi dostępnymi z tej
      // sesji do czasu zweryfikowania kodu. Stan `pendingTwoFactor` na froncie jest tylko
      // pomocniczy — prawdziwym sygnałem stanu jest sesja po stronie BE.

      // Weryfikuje 6-cyfrowy kod logowania. Po sukcesie BE zwraca pełny TokenResponse
      // (taki sam jak normalny `/login`) i zwalnia sesję. Zwraca true/false.
      async verifyTwoFactorCode(code) {
          const preAuthToken = this.pendingTwoFactor?.preAuthToken ?? null;
          try {
              const res = await fetchWrapper.post(
                  `${process.env.VUE_APP_API_URL}/login/2fa/verify`,
                  { code },
                  preAuthToken
              );
              if (!res || !res.token) {
                  return false;
              }
              this.token = res.token;
              sessionStorage.setItem("cvToken", res.token);
              this.pendingTwoFactor = null;

              const userStore = useUserStore();
              userStore.reset();

              const passwordExpired = await this.ensurePasswordFreshness();
              if (passwordExpired) {
                  this.notify.warning(i18n.global.t("password-expired-set-new"));
                  router.push("/password-expired");
              } else {
                  this.startInactivityWatch();
                  this.ping(false);
                  const userStoreForLanding = useUserStore();
                  await this.prepareAppContext(userStoreForLanding);
                  const target = this.resolvePostLoginTarget(userStoreForLanding);
                  router.push(target);
              }
              return true;
          } catch {
              return false;
          }
      },

      // Ponowna wysyłka kodu 2FA (resend). Aktualizuje pendingTwoFactor.challenge.
      async resendTwoFactorCode() {
          const preAuthToken = this.pendingTwoFactor?.preAuthToken ?? null;
          const res = await fetchWrapper.post(
              `${process.env.VUE_APP_API_URL}/login/2fa/resend`,
              undefined,
              preAuthToken
          );
          if (res && res.challenge && this.pendingTwoFactor) {
              this.pendingTwoFactor = {
                  ...this.pendingTwoFactor,
                  challenge: res.challenge,
                  dispatchFailed: false,
              };
          }
          return res;
      },

      // Ustawia numer telefonu admina (świeże konto) i wysyła pierwszy kod 2FA.
      async setupTwoFactorPhone(phoneNumberDir, phoneNumber) {
          const preAuthToken = this.pendingTwoFactor?.preAuthToken ?? null;
          const res = await fetchWrapper.post(
              `${process.env.VUE_APP_API_URL}/login/2fa/setup-phone`,
              { phoneNumberDir, phoneNumber },
              preAuthToken
          );
          if (res && res.challenge) {
              this.pendingTwoFactor = {
                  // preAuthToken pozostaje ten sam — to nadal ta sama sesja BE
                  preAuthToken,
                  requiresPhoneSetup: false,
                  challenge: res.challenge,
                  dispatchFailed: false,
              };
              router.push("/login/2fa");
          }
          return res;
      },

      clearPendingTwoFactor() {
          this.pendingTwoFactor = null;
      },

      // 🔍 Sprawdź ważność hasła (3 miesiące od updated_at)
    // async ensurePasswordFreshness() {
    //   try {
    //     const userStore = useUserStore();
    //     if (typeof userStore.self === "function") {
    //       await userStore.self();
    //     }

    //     const user = userStore.currentUser || {};

    //     console.log('user:', JSON.stringify(user));
    //     // Elastycznie: bierzemy cokolwiek serwer zwraca
    //     const updatedAtRaw = user.passwordUpdatedAt || user.updatedAt;

    //     if (!updatedAtRaw) {
    //       // jeśli nie mamy daty – uznajemy, że nie wygasło
    //       return false;
    //     }

    //     const updatedAt = new Date(updatedAtRaw);
    //     if (isNaN(updatedAt.getTime())) {
    //       // jeśli data niepoprawna – uznajemy, że nie wygasło
    //       return false;
    //     }

    //     // 3 miesiące od updated_at
    //     const expiry = new Date(updatedAt.getTime());
    //     expiry.setMonth(expiry.getMonth() + 3);

    //     return new Date() > expiry; // true => hasło wygasło
    //   } catch {
    //     // w razie błędu nie blokuj użytkownika – traktuj jako ważne
    //     return false;
    //   }
    // },

    async ensurePasswordFreshness() {
      try {
        // 0) Jednorazowa amnestia zaraz po zmianie hasła
        if (this.skipPwdFreshCheckOnce) {
          this.skipPwdFreshCheckOnce = false;
          return false; // NIE traktuj jako wygasłe
        }

        const userStore = useUserStore();
        if (typeof userStore.self === "function") {
          await userStore.self();
        }
        const u = userStore.currentUser || {};

        // helper do parsowania daty
        const asDate = (v) => {
          if (!v) return null;
          const d = new Date(v);
          return isNaN(d) ? null : d;
        };

        // 1) Jeśli mamy lokalny znacznik zmiany – użyj go (eliminuje lag backendu)
        let changedAt =
          (this.localPasswordChangedAt && new Date(this.localPasswordChangedAt)) ||
          asDate(u.passwordUpdatedAt) ||
          asDate(u.password_updated_at) ||
          asDate(u.updatedAt) ||
          asDate(u.updated_at);

        // 2) Fallback – jeśli mamy passwordExpiration, to też możemy użyć
        const explicitExpiration =
          asDate(u.passwordExpiration) || asDate(u.password_expiration);

        if (changedAt) {
          const expiry = new Date(changedAt.getTime());
          expiry.setMonth(expiry.getMonth() + 3); // 3 miesiące ważności
          return new Date() > expiry;
        }

        if (explicitExpiration) {
          return new Date() > explicitExpiration;
        }

        // Brak jakichkolwiek dat => nie blokuj
        return false;
      } catch {
        // W razie błędu nie blokuj
        return false;
      }
    },

    // Włącza/wyłącza odświeżanie sesji backendu przez aktywność użytkownika
    async ping(disable) {
      if (disable) {
        this.sessionTouchEnabled = false;
        this.pingLoop = false;
      } else {
        if (!this.token) {
          this.sessionTouchEnabled = false;
          this.pingLoop = false;
          return;
        }
        this.sessionTouchEnabled = true;
        this.pingLoop = true;
        await this.touchSession("ping-enable");
      }
    },

    // 🔒 Start/Stop auto-logout po 5 min braku aktywności
    // startInactivityWatch() {
    //   this.stopInactivityWatch(); // safety
    //   this._activityEvents = [
    //     "mousemove",
    //     "keydown",
    //     "click",
    //     "touchstart",
    //     "scroll",
    //   ];
    //   this._boundActivityHandler = () => this.resetInactivityTimer();
    //
    //   this._activityEvents.forEach((evt) => {
    //     window.addEventListener(evt, this._boundActivityHandler, {
    //       passive: true,
    //     });
    //   });
    //
    //   this.resetInactivityTimer();
    // },
    //
    // stopInactivityWatch() {
    //   if (this._activityEvents && this._boundActivityHandler) {
    //     this._activityEvents.forEach((evt) => {
    //       window.removeEventListener(evt, this._boundActivityHandler);
    //     });
    //   }
    //   this._activityEvents = null;
    //   this._boundActivityHandler = null;
    //
    //   if (this.inactivityTimer) {
    //     clearTimeout(this.inactivityTimer);
    //     this.inactivityTimer = null;
    //   }
    // },

    // resetInactivityTimer() {
    //   if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    //   // 5 minut = 300000 ms
    //   this.inactivityTimer = setTimeout(() => {
    //     this.notify.error(
    //       "Zostałeś wylogowany z powodu braku aktywności (5 min)."
    //     );
    //     this.logout();
    //   }, 5 * 60 * 1000);
    // },

    // onPasswordChanged: async function() {
    //   try {
    //     const userStore = useUserStore();
    //     await userStore.self?.(); // świeży updated_at/passwordUpdatedAt
    //   } catch {
    //     // ignoruj
    //   }
    //   this._setMustChangePassword(false);
    //   this.startInactivityWatch();
    //   this.ping(false);
    // },
      startInactivityWatch() {
          this.stopInactivityWatch(); // safety

          // Prawdziwa aktywność użytkownika = odświeżenie sesji na backendzie
          this._activityEvents = ["mousemove", "keydown", "click", "touchstart", "scroll"];
          this._onUserActivity = () => this.onUserActivity();

          this._activityEvents.forEach((evt) => {
              window.addEventListener(evt, this._onUserActivity, { passive: true });
          });

          // Gdy karta wraca na wierzch, odśwież sesję tylko raz.
          this._onVisibilityChange = () => {
              if (!document.hidden) this.heartbeat("visibilitychange");
          };
          document.addEventListener("visibilitychange", this._onVisibilityChange);

          // Widoczna karta = aktywność (heartbeat pomija document.hidden). Backend przedłuża sesję tylko przez /ping,
          // więc odpytywanie w tle z ukrytej karty nie blokuje wygaśnięcia sesji.
          this._visibleTabTimer = setInterval(() => this.heartbeat("visible-tab"), VISIBLE_TAB_HEARTBEAT_MS);

          // Sygnał przy zamykaniu/odświeżeniu karty.
          this._onBeforeUnload = () => this.sendLeaveRequest();
          window.addEventListener("beforeunload", this._onBeforeUnload);
      },

      stopInactivityWatch() {
          if (this._activityEvents && this._onUserActivity) {
              this._activityEvents.forEach((evt) => {
                  window.removeEventListener(evt, this._onUserActivity);
              });
          }
          if (this._onVisibilityChange) {
              document.removeEventListener("visibilitychange", this._onVisibilityChange);
          }
          if (this._onBeforeUnload) {
              window.removeEventListener("beforeunload", this._onBeforeUnload);
          }
          if (this._visibleTabTimer) {
              clearInterval(this._visibleTabTimer);
          }
          this._activityEvents = null;
          this._onUserActivity = null;
          this._onVisibilityChange = null;
          this._onBeforeUnload = null;
          this._visibleTabTimer = null;

      },

      onUserActivity() {
          this.heartbeat("user-activity");
      },

      /**
       * “Bicie serca” z aplikacji (np. po aktywności użytkownika).
       * Frontend nie trzyma już sesji stałym interwałem.
       */
      heartbeat(source = "app") {
          if (!this.sessionTouchEnabled || !this.token) return;
          if (document.hidden) return;

          this.touchSession(source);
      },

      async touchSession(source = "activity") {
          if (!this.token) return;

          const now = Date.now();
          if (now - this.lastSessionTouchAt < SESSION_TOUCH_INTERVAL_MS) return;

          this.lastSessionTouchAt = now;

          try {
              await fetchWrapper.get(`${process.env.VUE_APP_API_URL}/ping`);
          } catch (error) {
              // 401/403 obsługuje globalnie fetchWrapper
              console.debug("Session touch failed", source, error);
          }
      },
    sendLeaveRequest() {
      // Wersje nieprodukcyjne (test / lokalny dev): NIE ubijaj sesji backendu przy
      // zamknięciu/odświeżeniu karty. Token API = ID sesji PHP (trzymanej w DB), więc po F5
      // token (sessionStorage) i sesja BE nadal żyją — użytkownik nie zostaje wylogowany,
      // a aplikacja i tak przeładowuje się od nowa (świeże dane).
      if (isPersistSessionOnReload) {
        return;
      }

      if (!this.token || typeof fetch === "undefined") {
        return;
      }

      fetch(`${process.env.VUE_APP_API_URL}/login`, {
        method: "DELETE",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      }).catch(() => {});
    },
    onPasswordChanged: async function() {
      // 1) zdejmij wymuszanie zmiany hasła
      this._setMustChangePassword(false);

      // 2) ustaw lokalny „czas zmiany” i jednorazowe pominięcie sprawdzania
      this.localPasswordChangedAt = new Date();
      this.skipPwdFreshCheckOnce = true;

      // 3) spróbuj dociągnąć świeżego usera (jak backend już zaktualizował daty)
      //    razem z flagami funkcji — z tego ekranu użytkownik wchodzi wprost do
      //    aplikacji, więc menu musi mieć komplet danych przed pierwszym renderem.
      await this.prepareAppContext();

      // 4) wznów zegary sesji
      this.startInactivityWatch();
      this.ping(false);
    },

    // Wylogowuje użytkownika (usuwa token, czyści wszystko, przekierowuje na /login)
    logout() {
      const current = useCurrentStore();
      current.stopLogout(); // czyści interwały i dane bieżące

      // Ewidencja dostępu do stacji preselekcji: otwarte wpisy zamyka backend przy niszczeniu sesji,
      // tutaj kasujemy tylko pamięć ostatnio wysłanego stanu, żeby kolejne logowanie zaczęło od zera.
      useLocationSessionStore().reset();

      // zatrzymaj ping i nasłuch bezczynności
      this.ping(true);
      this.stopInactivityWatch();

      fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/login`);
      this.token = null;
      this.pingLoop = false;
      this.sessionTouchEnabled = false;
      this.lastSessionTouchAt = 0;
      sessionStorage.removeItem("cvToken");
      // Zdjęcia pojazdów trzymane w pamięci (cache listy/szczegółów) nie mogą zostać po wylogowaniu.
      clearImageCache();
        const userStore = useUserStore();
        userStore.reset();
        router.push("/login");
    },

    // Resetowanie hasła. Backend w odpowiedzi wysyła maila z linkiem do formularza
    // zmiany hasła, więc na froncie nie ma już potrzeby przekierowywać użytkownika —
    // pozostaje na widoku resetu, gdzie wyświetlany jest komunikat sukcesu.
    async resetPassword(email) {
      await fetchWrapper.put(
        `${process.env.VUE_APP_API_URL}/reset-password`,
        { email }
      );
    },

    // Wysyła żądanie SMS-a z 6-cyfrowym kodem jednorazowym do resetu hasła.
    // Backend zawsze zwraca 204 (nie zdradza, czy konto istnieje). Po sukcesie zapisujemy
    // numer telefonu w state, żeby widok weryfikacji mógł go użyć — bez przerzucania PII
    // przez URL/sessionStorage.
    async requestSmsResetCode(phoneNumberDir, phoneNumber) {
      await fetchWrapper.put(
        `${process.env.VUE_APP_API_URL}/reset-password/sms`,
        { phoneNumberDir, phoneNumber }
      );
      this.pendingSmsReset = { phoneNumberDir, phoneNumber };
    },

    // Weryfikuje 6-cyfrowy kod jednorazowy. Po sukcesie BE zwraca standardowy token
    // bundle'a SymfonyCasts — front przekazuje go dalej do `/reset-password/reset?token=...`,
    // gdzie ten sam ChangePasswordView (co dla email-resetu) ustawia nowe hasło.
    // Zwraca string token w razie sukcesu, null w razie niepowodzenia.
    async verifySmsResetCode(code) {
      if (!this.pendingSmsReset) {
        return null;
      }
      try {
        const { phoneNumberDir, phoneNumber } = this.pendingSmsReset;
        const response = await fetchWrapper.post(
          `${process.env.VUE_APP_API_URL}/reset-password/sms/verify`,
          { phoneNumberDir, phoneNumber, code }
        );
        return response?.token ?? null;
      } catch {
        return null;
      }
    },

    clearPendingSmsReset() {
      this.pendingSmsReset = null;
    },

    // Ustawienie nowego hasła po wejściu z linka z maila (token z URL).
    // BE identyfikuje użytkownika po samym tokenie — email nie jest przesyłany.
    async setPassword(password, token) {
      try {
        const response = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/set-password`,
          { password, token }
        );
        return response;
      } catch (error) {
        // console.log("Error in setPassword:", error);
        return error;
      }
    },

    // Czyści komunikat o błędzie (np. po wyświetleniu na UI)
    clearErrorMessage() {
      // Powiadomienia gasną same — nie ma czego czyścić przed żądaniem.
    },
  },
});
